import axios from "axios";

// The API contract version this client was built against. The backend stamps
// every contract-conformant response with `X-WL-Contract: 1` (see
// 02-api-contract.md / 07-web-contract-alignment.md). This interceptor is the
// client-side tripwire for contract drift: it warns when an `/api/` response
// is missing the header or carries an unexpected version.
export const EXPECTED_CONTRACT_VERSION = "1";

const isApiResponse = (response) => {
  const url = response?.config?.url || "";
  // Calls are made with relative `/api/...` paths against the same origin.
  return url.includes("/api/");
};

const readContractHeader = (response) => {
  const headers = response?.headers;
  if (!headers) return undefined;
  // axios lowercases header keys; fall back to .get for AxiosHeaders instances.
  if (typeof headers.get === "function") {
    return headers.get("x-wl-contract") ?? headers["x-wl-contract"];
  }
  return headers["x-wl-contract"];
};

const checkContract = (response) => {
  if (!response || !isApiResponse(response)) return;
  const version = readContractHeader(response);
  if (version === undefined || version === null || version === "") {
    console.warn(
      `[contract] Missing X-WL-Contract header on ${response.config?.url} ` +
        `(expected "${EXPECTED_CONTRACT_VERSION}"). Backend may be pre-contract.`
    );
    return;
  }
  if (String(version) !== EXPECTED_CONTRACT_VERSION) {
    console.warn(
      `[contract] X-WL-Contract mismatch on ${response.config?.url}: ` +
        `got "${version}", expected "${EXPECTED_CONTRACT_VERSION}".`
    );
  }
};

let installed = false;

// Registers a single global axios response interceptor. Idempotent: safe to
// call more than once (e.g. across hot reloads) without stacking interceptors.
export const installContractGuard = () => {
  if (installed) return;
  installed = true;
  axios.interceptors.response.use(
    (response) => {
      checkContract(response);
      return response;
    },
    (error) => {
      // Inspect error responses too, but never swallow the rejection.
      if (error?.response) checkContract(error.response);
      return Promise.reject(error);
    }
  );
};
